import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "react-oidc-context";

const EditHotelModal = ({ isOpen, onClose, hotel, onSuccess }) => {
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [installationDate, setInstallationDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [applyStaysZeroing, setApplyStaysZeroing] = useState(true);

  const auth = useAuth();
  const token = auth.user?.access_token;
  const API_URL = process.env.REACT_APP_API_URL || "/api";

  useEffect(() => {
    if (!isOpen || !hotel?.id || !token) return;

    setName(hotel.name || "");
    setCity("");
    setInstallationDate("");
    setApplyStaysZeroing(true);

    const fetchProfile = async () => {
      setLoadingProfile(true);
      try {
        const { data } = await axios.get(`${API_URL}/hotels/${hotel.id}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (data?.success === "OK" && data.result) {
          setName(data.result.name || hotel.name || "");
          setCity(data.result.city || "");
          setInstallationDate(data.result.installationDate || "");
        }
      } catch (error) {
        console.error("Erreur chargement profil hôtel:", error);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [isOpen, hotel, token, API_URL]);

  const handleSave = async () => {
    if (!hotel?.id) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      alert("Le nom de l'hôtel est requis");
      return;
    }
    const trimmedCity = city.trim();
    if (!trimmedCity) {
      alert("La ville de l'hôtel est requise");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.patch(
        `${API_URL}/hotels/${hotel.id}/profile`,
        {
          name: trimmedName,
          city: trimmedCity,
          installationDate: installationDate || null,
          applyStaysZeroing,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (data?.success !== "OK") {
        throw new Error(data?.message || "Erreur lors de la sauvegarde");
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Erreur mise à jour hôtel:", error);
      const message = error.response?.data?.message || error.message || "Erreur lors de la sauvegarde";
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Modifier l&apos;hôtel</h3>
          <button type="button" onClick={handleClose} disabled={loading} className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {loadingProfile ? (
            <p className="text-sm text-gray-500">Chargement…</p>
          ) : (
            <>
              <div>
                <label htmlFor="hotel-edit-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de l&apos;hôtel
                </label>
                <input
                  id="hotel-edit-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="hotel-edit-installation-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Date d&apos;installation
                </label>
                <input
                  id="hotel-edit-installation-date"
                  type="date"
                  value={installationDate}
                  onChange={(e) => setInstallationDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Utilisée pour remettre à zéro les séjours théoriques avant cette date (réglages GreenMiles et KPI).
                </p>
              </div>

              <div>
                <label htmlFor="hotel-edit-city" className="block text-sm font-medium text-gray-700 mb-1">
                  Ville
                </label>
                <input
                  id="hotel-edit-city"
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                  placeholder="Ex: Paris"
                />
              </div>

              {installationDate ? (
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={applyStaysZeroing}
                    onChange={(e) => setApplyStaysZeroing(e.target.checked)}
                    disabled={loading}
                  />
                  <span>Recalculer les séjours théoriques (mois antérieurs à 0, prorata du mois d&apos;installation)</span>
                </label>
              ) : null}
            </>
          )}

          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || loadingProfile}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Sauvegarde…" : "Sauvegarder"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditHotelModal;
